import React, { useState, useRef, useEffect } from 'react';
import { customToast } from '../utils/toastUtils';
import './TicketChatModal.css';

const TicketChatModal = ({ show, onClose, ticket, userId, onTicketUpdated }) => {
  const [newMessage, setNewMessage] = useState('');
  const [isSending, setIsSending] = useState(false);
  const chatBodyRef = useRef(null);

  useEffect(() => {
    // Auto-scroll to bottom when messages change
    if (chatBodyRef.current) {
      chatBodyRef.current.scrollTop = chatBodyRef.current.scrollHeight;
    }
  }, [ticket]);

  if (!show || !ticket) return null;

  const getStatusColor = (status) => {
    switch (status) {
      case 'open': return { bg: '#dcfce7', text: '#166534', border: '#86efac', label: 'Open' };
      case 'in-progress': return { bg: '#fef3c7', text: '#92400e', border: '#fbbf24', label: 'In Progress' };
      case 'closed': return { bg: '#fee2e2', text: '#991b1b', border: '#fca5a5', label: 'Closed' };
      default: return { bg: '#f1f5f9', text: '#475569', border: '#cbd5e1', label: status };
    }
  };

  const statusInfo = getStatusColor(ticket.status);

  // Build chat messages — interleave descriptions (user) and responses (admin)
  // in strict chronological order based on each message's own timestamp
  const buildChatMessages = () => {
    const messages = [];

    // User descriptions
    let descriptions = ticket.descriptions || [];
    // Backward compatibility: if descriptions is empty but description (string) exists
    if (descriptions.length === 0 && ticket.description) {
      descriptions = [{
        text: ticket.description,
        timestamp: ticket.createdAt || new Date().toISOString()
      }];
    }

    descriptions.forEach((desc, idx) => {
      const text = typeof desc === 'string' ? desc : desc.text;
      const ts = typeof desc === 'string'
        ? (ticket.createdAt || new Date().toISOString())
        : (desc.timestamp || ticket.createdAt || new Date().toISOString());
      messages.push({
        type: 'user',
        text,
        timestamp: ts,
        _order: new Date(ts).getTime() || idx // fallback to index for stable sort
      });
    });

    // Admin responses — each should have {text, timestamp}
    const responses = ticket.responses || [];
    responses.forEach((resp, idx) => {
      const text = typeof resp === 'string' ? resp : resp.text;
      const ts = typeof resp === 'string' ? null : (resp.timestamp || null);
      messages.push({
        type: 'admin',
        text,
        timestamp: ts,
        _order: ts ? new Date(ts).getTime() : (idx + 1) // keep insertion order if no timestamp
      });
    });

    // Sort chronologically — descriptions and responses interleaved by timestamp
    messages.sort((a, b) => a._order - b._order);

    return messages;
  };

  const chatMessages = buildChatMessages();

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-IN', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const handleSendReply = async () => {
    if (!newMessage.trim()) {
      customToast.warning('Please type a message');
      return;
    }

    if (ticket.status === 'closed') {
      customToast.info('This ticket is closed. You cannot add more messages.');
      return;
    }

    setIsSending(true);

    try {
      const response = await fetch(
        `${import.meta.env.VITE_BACKEND_URL}/api/email/support/${ticket.id}/reply`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            message: newMessage.trim(),
            userId
          })
        }
      );

      const result = await response.json();

      if (result.success) {
        setNewMessage('');
        customToast.success('Message sent!');
        if (onTicketUpdated) onTicketUpdated();
      } else {
        throw new Error(result.error || 'Failed to send message');
      }
    } catch (error) {
      console.error('Error sending reply:', error);
      customToast.error('Failed to send message. Please try again.');
    } finally {
      setIsSending(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendReply();
    }
  };

  return (
    <div className="ticket-chat-overlay" onClick={onClose}>
      <div className="ticket-chat-modal" onClick={(e) => e.stopPropagation()}>
        {/* Top Close Button */}
        <button className="ticket-chat-close-top" onClick={onClose}>
          ×
        </button>

        {/* Header */}
        <div className="ticket-chat-header">
          <div className="ticket-chat-header-icon">
            <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </div>
          <div className="ticket-chat-header-info">
            <h3>Ticket #{ticket.id?.slice(-6).toUpperCase()}</h3>
            <div className="ticket-chat-meta">
              <span
                className="ticket-status-badge"
                style={{
                  background: statusInfo.bg,
                  color: statusInfo.text,
                  borderColor: statusInfo.border
                }}
              >
                <span className="status-dot" style={{ background: statusInfo.text }}></span>
                {statusInfo.label}
              </span>
              <span className="ticket-chat-category">{ticket.category}</span>
            </div>
          </div>
        </div>

        {/* Issue Tag */}
        <div className="ticket-chat-issue-tag">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M12 8v4M12 16h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
          {ticket.issue}
        </div>

        {/* Chat Body */}
        <div className="ticket-chat-body" ref={chatBodyRef}>
          {chatMessages.length === 0 ? (
            <div className="ticket-chat-empty">
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#cbd5e1" strokeWidth="1.5">
                <path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              <p>No messages yet</p>
            </div>
          ) : (
            chatMessages.map((msg, index) => (
              <div
                key={index}
                className={`chat-message ${msg.type === 'user' ? 'chat-message-user' : 'chat-message-admin'}`}
              >
                <div className="chat-message-avatar">
                  {msg.type === 'user' ? (
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2" strokeLinecap="round" strokeLinejoin="round"/>
                      <circle cx="12" cy="7" r="4" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  ) : (
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  )}
                </div>
                <div className="chat-message-content">
                  <div className="chat-message-sender">
                    {msg.type === 'user' ? 'You' : 'Support Team'}
                  </div>
                  <div className="chat-message-bubble">
                    {msg.text}
                  </div>
                  {msg.timestamp && (
                    <div className="chat-message-time">
                      {formatDate(msg.timestamp)}
                    </div>
                  )}
                </div>
              </div>
            ))
          )}
        </div>

        {/* Reply Input */}
        {ticket.status !== 'closed' ? (
          <div className="ticket-chat-input-area">
            <textarea
              className="ticket-chat-input"
              placeholder="Type your message..."
              value={newMessage}
              onChange={(e) => setNewMessage(e.target.value)}
              onKeyDown={handleKeyDown}
              rows="2"
              disabled={isSending}
            />
            <button
              className="ticket-chat-send-btn"
              onClick={handleSendReply}
              disabled={isSending || !newMessage.trim()}
            >
              {isSending ? (
                <svg className="send-spinner" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              ) : (
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M22 2L11 13M22 2l-7 20-4-9-9-4 20-7z" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              )}
            </button>
          </div>
        ) : (
          <div className="ticket-chat-closed-banner">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <rect x="3" y="11" width="18" height="11" rx="2" ry="2" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M7 11V7a5 5 0 0110 0v4" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            This ticket has been closed
          </div>
        )}

        {/* Bottom Close Button */}
        <div className="ticket-chat-bottom-close">
          <button className="ticket-chat-close-btn" onClick={onClose}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="18" y1="6" x2="6" y2="18" strokeLinecap="round" strokeLinejoin="round"/>
              <line x1="6" y1="6" x2="18" y2="18" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default TicketChatModal;
